import { SelectElementType } from './ui-components'
import {
  Bookies,
  ChannelDetailed,
  CountryModel,
  OddsFormat,
  PriceBreakdown,
  SportEventType,
  UserGender,
  UserOnboardingStages,
  UserRoles,
  WaitlistContinentsEnum,
  SportEventDetailed,
  SsoProviderConfig
} from '@app/graphql'

export type SsoProviderConfigList = SsoProviderConfig[]

export { OddsFormat }

export interface UserAuthStateType {
  userAppName: string
  countriesList: CountryModel[]
  userCountryId: string
  userLanguageId: string
  languagesList: { id: string; name: string; code: string }[]
  userBirthdayDate: Date
  favouriteSports: Set<string>
  favouriteBookies?: Set<string>
  favouriteMarkets?: Set<string>
  favouriteCompetitions: Map<string, Set<string>>
  affiliateLinkName: string
  inviteeTipsterLinkName: string
  inviteLinkName: string
  webpSupported: boolean
  firstName: string
  lastName: string
  followLinkName: string
  vipInviteToken: string
  translatedCountry: any
  userEmail?: string
  install?: string
  invalidMagicLink?: boolean
  noMagicLinkEmail?: boolean
}

export type CurrentCountry = Omit<CountryModel, 'continent'> & { continent: WaitlistContinentsEnum; loaded: boolean }
export enum ExtraUserRoles {
  Guest = 'GUEST',
}

export type AllUserRoles = UserRoles | ExtraUserRoles

export interface TourStages {
  basicWelcome: boolean
  basicVideoExplainer: boolean
  contentWelcome: boolean
  contentVideoExplainer: boolean
  contentTipComparison: boolean
  contentTipMenu: boolean
  ContentDataTip: boolean
  ContentImageTip: boolean
  subscriptionMenu: boolean
  subscriptionWelcome: boolean
  subscriptionVideoExplainer: boolean
  subcriptionServiceMenu: boolean
  subscriptionFeatureBanner: boolean
  userInviteWelcome: boolean
  userInviteMenu: boolean
  startAppBanner: boolean
  userCompletedOnboarding: boolean
}

export interface CurrentUserStateType {
  isTipsterAdmin: boolean
  isGuestTipster: boolean
  hasPendingSubscription: boolean
  initiateOnboarding: boolean
  userDataLoading: boolean
  userType: AllUserRoles
  userEmail: string
  username: string
  id: string
  name: string
  surname: string
  avatarUrl: string
  profileBackgroundUrl: string
  birthday: Date
  countryId: string
  bio: string
  phone: string
  authBySocial: boolean
  incognitoMode: boolean
  confirmedConditions: boolean
  gender: UserGender
  banned: boolean
  muted: boolean
  notedAboutMute: boolean
  permAddPost: boolean
  permEditPost: boolean
  onboardingStage?: UserOnboardingStages
  ownedChannels: Pick<ChannelDetailed, 'id' | 'channelName' | 'logo' | 'settings'>[]
  followedChannels: Pick<ChannelDetailed, 'id' | 'channelName' | 'logo' | 'settings'>[]
  auth_type: 'telegram' | 'facebook' | 'google' | 'magic_link'
  readonly guestMode: boolean
  readonly isTipster: boolean
  readonly isAdmin: boolean
  readonly isUser: boolean
  readonly hasOnboarded: boolean
  unitValue: number
  unitShare: number
  oddsFormat: OddsFormat
  tourStage: TourStages
  isTourCompleted: boolean
  currentTourStage: keyof TourStages
  readonly isTouring: boolean
  readonly currentChannelId: string
  favoriteTenantBookieId: string | null
}

export enum TipCreationStep {
  event = 'EVENT',
  bookie = 'BOOKIE',
  post = 'POST',
  chooseBookie = 'CHOOSE_BOOKIE',
  postTip = 'POST_TIP',
}

export interface Tip {
  tipTemporaryId: string
  sportId: string
  sportName: string
  countryId: string
  countryName: string
  competitionId: string
  competitionName: string
  eventDate: Date
  eventId: string
  eventName: string
  competitor1: string
  competitor2: string
  eventData: SportEventType
  marketId: string
  marketName: string
  outcomeId: string
  outcomeName: string
  odd: number
}

export interface TipCreationStateType extends Tip {
  creationStep: TipCreationStep
  searchEvent: string
  searchMode: boolean
}

export interface TipsCreationListStateType {
  tipsList: Tip[]
  isVIP: boolean
  tipsComment: string
  withComments: boolean
  tipAnalysis: string
  bookieName: string
  bookieId: string
  isCustomOdds: boolean
  tipStake: number
  bookieList: Bookies[]
  includeInStat: boolean
  videoUrl: string
  audioUrl: string
}

export type LiveMarketOddsType =
  Pick<SportEventDetailed, 'bookmakerId' | 'bookmakerName' | 'eventOutcome' | 'hasLiveOdds'>

export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: readonly string[]
  readonly userChoice: Promise<{
    readonly outcome: 'accepted' | 'dismissed'
    readonly platform: string
  }>
  prompt(): Promise<void>
}

export interface AppSettingsStateType {
  favouriteSports: Set<string>
  bookiesList: SelectElementType[]
  favouriteBookies: Set<string>
  leaguesList: SelectElementType[]
  favouriteCompetitions: Set<string>
  marketsList: SelectElementType[]
  favouriteMarkets: Set<string>
}

export interface MatchListStateType {
  sportId: string
  categoryId: string
  competitionId: string
  competitionName: string
  eventsDate: Date
  eventId: string
  favouriteEvents: Set<string>
}

export interface InboxStateType {
  expandedTipId: string
  featureName: string
  featureDescription: string
  conversationId: string
  conversationTitle: string
  conversationImageSrc: string
}

export interface PaymentStateType {
  paymentResult: string
  serviceTitle: string
  serviceSubtitle: string
  servicePrice: number
  serviceCurrency: string
  subscriptionId: string
  paymentTypeName: string
  paymentTypeId: string
  serviceDuration: number | string
  priceBreakdown: PriceBreakdown
  serviceCurrencySymbol: string
  channelName: string
  channelId: string
  tipsterId: string
  /** New plan/purchase flow */
  planId: string
  clientSecret: string
  purchaseId: string
}
